import './services.product-preorder';
import './services.slpixel';
import './services.location';
import './services.tracker';
import './services.cart';
import './services.product';
import { filter, find, isObject, omit, pick } from 'lodash-es';
import slEventBus, { eventMetaMap } from '../../utils/slEventBus';
import { PRODUCT_EVENTS } from '../constants/angular-events';

const EVENT = PRODUCT_EVENTS.VARIATION_DROPDOWN;

const EMIT_EVENT_MAP = {
  'pdp-variation-change': eventMetaMap.pdp.variationChanged,
};

app.directive('productVariationDropdown', [
  'productService',
  'cartService',
  'trackerService',
  '$filter',
  'locationService',
  'slPixelService',
  '$location',
  'productPreorderService',
  function (
    productService,
    cartService,
    trackerService,
    $filter,
    locationService,
    slPixelService,
    $location,
    productPreorderService,
  ) {
    return {
      restrict: 'AE',
      link: function (scope, element, attrs) {
        const sortTranslations = (obj = {}, keys = []) => ({
          ...pick(obj, keys),
          ...omit(obj, keys),
        });

        const translateKeys = Object.keys(
          scope.product?.variant_options?.[0]?.name_translations ?? {},
        );

        if (translateKeys.length > 0 && scope.product.variations?.length > 0) {
          scope.product.variations = scope.product.variations.map(
            (variation) => ({
              ...variation,
              fields: variation.fields.map((field) => ({
                ...field,
                name_translations: sortTranslations(
                  field.name_translations,
                  translateKeys,
                ),
              })),
              fields_translations: sortTranslations(
                variation.fields_translations,
                translateKeys,
              ),
            }),
          );
        }

        scope.variationName = getVariationName();

        function getVariationName() {
          const variantId =
            locationService.getQueryParams()?.variant_id ||
            scope.$parent.variantId;

          if (variantId) {
            const variation = scope.product.variations.find(
              ({ key }) => key === variantId,
            );
            return (
              variation?.fields.map((field) =>
                $filter('translateModel')(field.name_translations),
              ) || []
            );
          }

          return [];
        }

        function checkIsVariationFullyMatched(variation) {
          // Get list of matching variation fields
          // example: ["small", "red"]
          var variationSelected = filter(
            variation.fields,
            function (field, index) {
              return (
                scope.variationName[index] ==
                $filter('translateModel')(
                  variation.fields[index].name_translations,
                )
              );
            },
          );
          return variationSelected.length == variation.fields.length;
        }

        // memory pdp.variationChanged by last emitted variationId; query preselect
        let lastEmittedVariationId;

        // Shared by both scope events: VARIATION_LOADED seeds it so re-picking the
        // option auto-select already chose does not report a change. Separate
        // from lastEmittedVariationId, which is the slEventBus contract and is
        // only written when the emit-event attribute is present. (SL-52837)
        let lastEmittedKey;

        function emitVariationEvent(eventName, variation) {
          const variationId = variation?.key ?? null;
          if (
            eventName === EVENT.VARIATION_CHANGED &&
            variationId === lastEmittedKey
          ) {
            return;
          }
          lastEmittedKey = variationId;
          scope.$emit(eventName, {
            productId: scope.product._id,
            variationId,
          });
        }

        function updateVariationSelection(index) {
          scope.notEnoughStock = -1; //means that there is enough stock
          scope.notEnoughStockQty = -1;

          filterVariations(index);

          scope.variationSelected = find(
            scope.product.variations,
            (variation) => checkIsVariationFullyMatched(variation),
          );

          if (!scope.variationSelected) {
            scope.addItemQuantity = 1;
            scope.hasStock = false;
            scope.state.checkoutReady = false;
            return undefined;
          }
          // SL-33827 remove legacy code: sold out when scope.variationSelected.max_order_quantity <= 0
          // if (scope.checkoutReady) scope.checkoutReady = true; // checking for not have delivery option and payment method, hide price etc is in
          scope.state.checkoutReady = productService.validateCheckoutReady(
            scope.product,
            scope.variationSelected,
            cartService.getItemPrice({ product: scope.product }),
          );
          scope.addItemQuantity = 1;
          // get variation price or member_price or price_sale
          if (
            scope.variationSelected.price ||
            scope.variationSelected.member_price ||
            scope.variationSelected.price_sale
          ) {
            scope.price = productService.showVariationPrice(
              scope.variationSelected,
            );
          }

          trackerService.gaSendDetails(
            scope.product,
            scope.variationSelected,
            scope.products,
          );
          scope.products = null; //clear the related products as they are only for impressions
          var price = cartService.getItemPrice({
            product: scope.product,
            variation: scope.variationSelected,
          });
          var dollars = isObject(price) ? price.dollars : 0;
          trackerService.fbSendViewContent(
            scope.product,
            dollars,
            scope.variationSelected,
          );
          trackerService.track({
            type: trackerService.generalEventType.VIEW_CONTENT,
            data: {
              product: scope.product,
              value: dollars,
              variationSelected: scope.variationSelected,
            },
          });
          const selectEmitEventName = EMIT_EVENT_MAP[attrs.emitEvent];
          if (
            selectEmitEventName &&
            scope.variationSelected.key !== lastEmittedVariationId
          ) {
            lastEmittedVariationId = scope.variationSelected.key;
            slEventBus.emit(
              selectEmitEventName,
              {
                productId: scope.product._id,
                variationId: scope.variationSelected.key,
              }, // sticky: page-load selection happens before SDK listeners register
              { sticky: true },
            );
          }
          var wishlistBlockCenter = !scope.product.hide_price && scope.hasStock;
          angular
            .element('.Wishlist-addItem-block')
            [
              wishlistBlockCenter ? 'addClass' : 'removeClass'
            ]('Wishlist-block-center');

          var productDetailPagePathRegex = /^\/products\/.*/;
          if (productDetailPagePathRegex.test($location.path()))
            sendSlPixelTracking();

          return scope.variationSelected;
        }

        // Template-facing entry point. Internal callers use
        // updateVariationSelection directly, so a call through here means the
        // shopper changed the selection. (SL-52837)
        scope.onVariationChanged = function (index) {
          const variation = updateVariationSelection(index);
          // No SKU for this combination: keep the existing silence, the host has
          // no variation to report.
          if (variation) {
            emitVariationEvent(EVENT.VARIATION_CHANGED, variation);
          }
        };

        function sendSlPixelTracking() {
          var product = scope.product;
          var targetVariation = scope.variationSelected;
          if (!isObject(targetVariation) || !isObject(product)) return;

          var priceTarget = product.same_price ? product : targetVariation;
          var variationId = targetVariation.key;
          var salePrice = priceTarget.price_sale
            ? priceTarget.price_sale.dollars
            : 0;
          var memberPrice = priceTarget.member_price
            ? priceTarget.member_price.dollars
            : 0;
          var currency =
            priceTarget.price && priceTarget.price.currency_iso
              ? targetVariation.price.currency_iso
              : null;
          var trackingProductParams = {
            productID: product._id,
            variationID: variationId,
            name: $filter('translateModel')(product.title_translations),
            price: priceTarget.price && priceTarget.price.dollars,
            currency: currency,
            salePrice: salePrice,
            memberPrice: memberPrice,
          };
          return slPixelService.hdPageView('productDetail', {
            product: trackingProductParams,
            inStock: scope.hasStock,
          });
        }

        function filterVariations(variationIndex) {
          scope.product._cache = scope.product._cache || {};
          const state = productService.updateVariationOption(
            {
              optionList: scope.product.digest_variations || [],
              selectedList: scope.variationName || [],
            },
            scope.product,
            variationIndex,
            scope.product._cache,
          );

          scope.product.digest_variations = state.optionList;
          scope.variationName = state.selectedList;
        }

        function selectBestOption(variation_key) {
          var bestVariation =
            getRequestedVariation() ||
            getInStockVariation() ||
            scope.product.variations[0];
          scope.variationName[variation_key] = $filter('translateModel')(
            bestVariation.fields[variation_key].name_translations,
          );
        }

        var getUrlVariationId = function () {
          // TODO: update variationId params onVariationChange
          var urlParams = locationService.getQueryParams();
          return urlParams['variation'];
        };

        var getRequestedVariation = function () {
          var urlVariationId = getUrlVariationId();
          return find(scope.product.variations, { key: urlVariationId });
        };

        const getInStockVariation = function () {
          const isOutOfStockOrderable = productService.isOutOfStockOrderable(
            scope.product,
          );

          return scope.product.variations.find(function (variation) {
            if (scope.product.unlimited_quantity || isOutOfStockOrderable) {
              return true;
            }

            const totalOrderableQuantity =
              variation.quantity +
              productPreorderService.getPreorderLimit(scope.product, variation);
            return totalOrderableQuantity > 0;
          });
        };

        // variant selected from pdp
        var previewVariant = scope.product.variations.find(function (option) {
          return option.key === scope.variantId;
        });

        var watchProduct = scope.$watch('product', function (newValue) {
          if (newValue) {
            filterVariations(0);

            if (productService.hasSelectableVariations(scope.product)) {
              if (previewVariant) {
                previewVariant.variant_option_ids.forEach(function (index) {
                  updateVariationSelection(index);
                });
              } else {
                scope.product.digest_variations.forEach(function (v, index) {
                  selectBestOption(index);
                });
                updateVariationSelection();
              }

              // Auto-select is settled, so report it once. Inside the branch on
              // purpose: without a picker there is no variation to report, and the
              // host already reports those products itself. The watcher
              // deregisters itself below, so this cannot fire twice.
              emitVariationEvent(
                EVENT.VARIATION_LOADED,
                scope.variationSelected,
              );
            }

            watchProduct();
          }
        });

        scope.getSelectTypeByIndex = function (index) {
          return productService.getVariationType(
            scope.product.field_titles[index].selectorEnabled,
          );
        };
      },
    };
  },
]);
